import { getMessage } from '@tcc/i18n';
import { SEASON_AGNOSTIC } from '../constants/seasonIds';
import {
  FIELD_SUMMARY,
  PLANTING_CATEGORY,
  PLANTING_IDS,
  HARVEST_CATEGORY,
  HARVEST_IDS,
  IN_SEASON_CATEGORY,
  IN_SEASON_IDS,
  BASE_LAYERS_CATEGORY,
  BASE_LAYER_IDS,
  SOILSURGO_LAYERS,
  IN_SEASON,
} from '../constants/mapLayerIds';
import { filterSoilSurgoLayers } from '../utils/getsoilsSurgoLayer';
import { FieldFeature } from '../types';

/**
 * Return an array without the suboption to hide.
 * @param {Array} subOptions
 * @param {String} subOptionToHideId
 * @return {Array}
 */
export const hideSubOption = (subOptions, subOptionToHideId) => subOptions.filter(obj => obj.id !== subOptionToHideId);;

/**
 * Returns an array of subOption map layers for the provided ids. For each subOption id,
 * if it is not present in the layers object, return that layer as disabled.
 * @param {Object} layers - Object of all map layers loaded in the app
 * @param {Array} ids - Array of map layer IDs to be returned
 * @param {String} seasonId
 * @param {Array} [disabledLayers=[]] the layers that are not to appear
 * @return {Array} - Array of map layer objects
 */
export const getSubOptionLayers = (
  layers,
  ids,
  seasonId,
  disabledLayers = []
) => {
  const filteredIds = ids.filter(
    (id) => ![...SOILSURGO_LAYERS, ...disabledLayers].includes(id)
  );
  return filteredIds.map((id) => {
    const layer = layers[seasonId][id] || layers[SEASON_AGNOSTIC][id];
    return {
      id,
      name: getMessage(`layer.${(layer && layer.nameId) || id}`),
      disabled: !layer,
    };
  });
};


/**
 * Check and remove those premium features that the user doesnt have
 * the needed asset on their subscription tier.
 * @param {Array} subOptions
 * @param {Array} fieldAssets
 * @return {Array}
 */
export const checkInSeasonSubOptionsAssets = (subOptions, fieldAssets, hideSpatialDisplayFeatureLDFlag = false) => {
  let newSubOptions = [...subOptions];
  if (hideSpatialDisplayFeatureLDFlag && !fieldAssets.includes(FieldFeature.FIELD_VIEW_SPATIAL_DISPLAY)) {
    newSubOptions = hideSubOption(newSubOptions, IN_SEASON.APPLICATIONS);
  }
  if (!fieldAssets.includes(FieldFeature.FIELD_HEALTH_ADVISOR)) {
    newSubOptions = hideSubOption(newSubOptions, IN_SEASON.FHA);
  }
  return newSubOptions;
}

/**
 * Returns an array of all map layer objects. Some map layer objects returned are simple
 * (for example, Field Summary). Most are held within sublists (for example, Planting).
 * Each sublist has name and disabled values. Each map layer object has both of those
 * values along with an id value, which is used in the URL hash and with the Selector
 * Crate component.
 * @param {String} seasonId
 * @param {Object} layers - List of all map layers
 * @param {Array} [disabledLayers=[]] the layers that are not to appear
 * @return {Array}
 */
export const getMapLayers = (seasonId, layers, disabledLayers = [], fieldAssets, hideSpatialDisplayFeatureLDFlag = false) => {
  const plantingSubOptions = getSubOptionLayers(
    layers,
    PLANTING_IDS,
    seasonId,
    disabledLayers
  );
  const harvestSubOptions = getSubOptionLayers(
    layers,
    HARVEST_IDS,
    seasonId,
    disabledLayers
  );
  let baseLayerSubOptions = getSubOptionLayers(
    layers,
    BASE_LAYER_IDS,
    seasonId,
    disabledLayers
  );
  const inSeasonSubOptions = getSubOptionLayers(
    layers,
    IN_SEASON_IDS,
    seasonId,
    disabledLayers
  );

  baseLayerSubOptions = filterSoilSurgoLayers(
    baseLayerSubOptions,
    layers[SEASON_AGNOSTIC].soilssurgo
  );

  const plantingEnabled = plantingSubOptions.some(
    (subOption) => !subOption.disabled
  );
  const harvestEnabled = harvestSubOptions.some(
    (subOption) => !subOption.disabled
  );
  const baseLayersEnabled = baseLayerSubOptions.some(
    (subOption) => !subOption.disabled
  );
  const inSeasonEnabled = inSeasonSubOptions.some(
    (subOption) => !subOption.disabled
  );

  return [
    {
      name: getMessage(`layer.${FIELD_SUMMARY}`),
      id: FIELD_SUMMARY,
    },
    ...(hideSpatialDisplayFeatureLDFlag && !fieldAssets.includes(FieldFeature.FIELD_VIEW_SPATIAL_DISPLAY) ? [] : [{
      name: getMessage('layer.planting'),
      id: PLANTING_CATEGORY,
      disabled: !plantingEnabled,
      subOptions: plantingSubOptions,
    }]),
    {
      name: getMessage('layer.inSeason'),
      id: IN_SEASON_CATEGORY,
      disabled: !inSeasonEnabled,
      subOptions: checkInSeasonSubOptionsAssets(inSeasonSubOptions, fieldAssets, hideSpatialDisplayFeatureLDFlag)
    },
    ...(hideSpatialDisplayFeatureLDFlag && !fieldAssets.includes(FieldFeature.FIELD_VIEW_SPATIAL_DISPLAY) ? [] : [{
      name: getMessage('layer.harvest'),
      id: HARVEST_CATEGORY,
      disabled: !harvestEnabled,
      subOptions: harvestSubOptions,
    }]),
    {
      name: getMessage('layer.baseLayers'),
      id: BASE_LAYERS_CATEGORY,
      disabled: !baseLayersEnabled,
      subOptions: baseLayerSubOptions,
    },
  ];
};